import React from 'react';
import { ConfigProvider, Layout } from 'antd';
import { useTranslation } from 'react-i18next';
// Registered at boot (`src/i18n.ts`): no copy added to the initial bundle.
import { NS } from '../../i18n';
// Component files, never the feature barrel: this is in the initial bundle
// (RouteSkeleton, the agent layouts), and the barrel would pull every screen.
import { Bone, SkeletonStatus, TextBone } from './CampaignSkeleton';
import type { CampaignsSkeletonKind } from './isCampaignsPath';
import { CampaignHeadingSkeleton } from '../CampaignHeading/CampaignHeading';
import {
  DetailActionsSkeleton,
  PerformanceBodySkeleton,
} from '../CampaignDetailPage/DetailSkeleton';
import { ContactsSkeleton } from '../CampaignResultsTable/ContactsSkeleton';
import {
  CampaignsTableSkeleton,
  CampaignsToolbarSkeleton,
} from '../CampaignsTable/CampaignsTableSkeleton';
import { CAMPAIGNS_HEADER_THEME } from '../CampaignsPage/headerTheme';
// The pages' own sheets: the skeleton is drawn in the pages' classes, and it
// paints before their chunks bring them. All scoped to campaign classes.
import '../../../common/components/PageShell/PageShell.css';
import '../CampaignsPage/CampaignsPage.css';
import '../CampaignDetailPage/CampaignDetailPage.css';
import '../CampaignPerformance/CampaignPerformance.css';
import '../CampaignResultsTable/CampaignResultsTable.css';
import '../CampaignWizardPage/CampaignWizardPage.css';
import '../ObjectiveStep/ObjectiveStep.css';
import '../WizardSection/WizardSection.css';
import '../StepField/StepField.css';
import './CampaignRouteSkeleton.css';

const { Content } = Layout;

const CONTROL = 'var(--silia-ctl-height)';

/** AppBreadcrumb's place: one 22px line of crumbs, then the page gap. */
const CrumbSkeleton: React.FC = () => (
  <div className="campaign-route-skeleton__crumb" aria-hidden="true">
    <TextBone width="min(260px, 70%)" lineHeight={22} />
  </div>
);

/**
 * `CampaignsPage` as it first paints: PageShell's header (title at the
 * campaigns' 28/36, the create button), the full-bleed divider, then
 * `CampaignsTable` loading — its toolbar and the very table skeleton it draws.
 * Inside the page's own header theme, so AntD scopes it the same way.
 */
const ListSkeleton: React.FC = () => (
  <ConfigProvider theme={CAMPAIGNS_HEADER_THEME}>
    <Content className="page-shell page-shell--flush campaigns-page">
      <CrumbSkeleton />
      <div className="page-shell__card">
        <header className="page-shell__header" aria-hidden="true">
          <TextBone width="min(200px, 60%)" size={28} lineHeight={36} />
          <div className="page-shell__actions">
            <Bone width={160} height={CONTROL} />
          </div>
        </header>
        <div className="page-shell__content">
          <div className="campaigns-page__divider" role="presentation" />
          <div className="campaigns-list">
            <CampaignsToolbarSkeleton />
            <CampaignsTableSkeleton />
          </div>
        </div>
      </div>
    </Content>
  </ConfigProvider>
);

/**
 * `CampaignDetailPage` before the listing answers — the first of its two
 * loading phases: nothing known but the route, so the heading's shape, the
 * actions' and Performance's (where a campaign that ran opens).
 */
const DetailSkeleton: React.FC = () => {
  const { t } = useTranslation(NS);
  return (
    <Content className="page-shell page-shell--flush campaign-detail">
      <CrumbSkeleton />
      <div className="page-shell__card campaign-detail__card">
        <header className="campaign-detail__head">
          <CampaignHeadingSkeleton />
          <div className="campaign-detail__head-actions">
            <DetailActionsSkeleton />
          </div>
        </header>
        <div className="campaign-detail__head-divider" role="presentation" />
        <SkeletonStatus label={t('loading.campaign')} testId="campaign-detail-skeleton">
          <PerformanceBodySkeleton />
        </SkeletonStatus>
      </div>
    </Content>
  );
};

/** `CampaignResultsPage` loading: the heading's shape and the full-height table. */
const ResultsSkeleton: React.FC = () => {
  const { t } = useTranslation(NS);
  return (
    <Content className="page-shell page-shell--flush campaign-detail">
      <CrumbSkeleton />
      <div className="page-shell__card campaign-detail__card">
        <CampaignHeadingSkeleton />
        <ContactsSkeleton label={t('loading.contacts')} full />
      </div>
    </Content>
  );
};

/** A numbered section's head (`WizardSection`): badge, title, hint. */
const SectionHeadSkeleton: React.FC = () => (
  <div className="wizard-section__head">
    <div className="wizard-section__heading">
      <Bone width={24} height={24} shape="circle" className="campaign-detail__badge-bone" />
      <div className="wizard-section__text campaign-detail__section-text">
        <TextBone width="min(220px, 60%)" size={16} lineHeight={24} />
        <TextBone width="min(420px, 90%)" size={12} lineHeight={20} />
      </div>
    </div>
  </div>
);

/** The six objective cards (`CAMPAIGN_OBJECTIVES`). */
const OBJECTIVE_CARDS = [0, 1, 2, 3, 4, 5];

/**
 * `CampaignWizardPage` on arrival — it has no loading phase of its own: the
 * shell draws at once on step 1 (name + objective), which has no summary aside
 * (that one starts at step 2). Header (title, the "step 1 of 4" line; the
 * stepper, back and next), the divider, then step 1's two sections.
 */
const CreateSkeleton: React.FC = () => (
  <Content className="page-shell page-shell--flush campaign-wizard">
    <CrumbSkeleton />
    <div className="page-shell__card campaign-wizard__card" aria-hidden="true">
      <header className="campaign-wizard__head">
        <div className="campaign-wizard__heading">
          <TextBone width="min(280px, 70%)" size={30} lineHeight={38} />
          <TextBone width={140} size={12} lineHeight={22} />
        </div>
        <div className="campaign-wizard__head-actions">
          <Bone
            width="min(360px, 100%)"
            height={CONTROL}
            className="campaign-route-skeleton__stepper"
          />
          <Bone width={40} height={CONTROL} />
          <Bone width={104} height={CONTROL} />
        </div>
      </header>
      <div className="campaign-wizard__head-divider" />
      <div className="objective-step">
        <section className="wizard-section">
          <SectionHeadSkeleton />
          <div className="objective-step__name-input">
            <Bone height={CONTROL} />
          </div>
          <div className="campaign-field objective-step__field">
            <TextBone width={120} size={12} lineHeight={20} />
            {/* The description's two-row textarea. */}
            <Bone height={64} />
            <TextBone width={64} size={12} lineHeight={20} />
          </div>
        </section>
        <section className="wizard-section">
          <SectionHeadSkeleton />
          <div className="objective-step__grid">
            {OBJECTIVE_CARDS.map((card) => (
              <div key={card} className="objective-step__card">
                <Bone width={40} height={40} />
                <div className="campaign-route-skeleton__card-text">
                  <TextBone width="55%" size={16} lineHeight={24} />
                  <TextBone width="90%" size={12} lineHeight={20} />
                  <TextBone width="70%" size={12} lineHeight={20} />
                </div>
                <div className="campaign-route-skeleton__card-text">
                  <TextBone width="35%" size={12} lineHeight={20} />
                  <TextBone width="85%" size={12} lineHeight={20} />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  </Content>
);

export interface CampaignRouteSkeletonProps {
  /** Which campaigns screen — `campaignsSkeletonKind(pathname)`. */
  kind: CampaignsSkeletonKind;
  /**
   * Adds the agent screens' 24px gutter (`AgentShell` in `AssistantDetailLayout`),
   * for `RouteSkeleton`, which draws it straight inside the app chrome. The
   * agent layouts and the routes' Suspense fallbacks are already inside it.
   */
  standalone?: boolean;
}

/**
 * Loading shape of the campaigns screens before their chunk arrives, after the
 * knowledge base's (SL-1909): RouteSkeleton (inside the app chrome), the agent
 * layouts while the agent loads and each route's Suspense fallback draw this,
 * and it is the SAME markup each page draws in its first loading phase — the
 * same exported skeleton pieces in the same page classes — so a refresh goes
 * route skeleton → page skeleton → data without a jump. Only the breadcrumb is
 * a bone here: its crumbs need the agent, which this paints before.
 *
 * The list, the detail and the full table carry their status inside (their
 * loading regions); the wizard, which has no loading phase, gets one here.
 */
export const CampaignRouteSkeleton: React.FC<CampaignRouteSkeletonProps> = ({
  kind,
  standalone = false,
}) => {
  const { t } = useTranslation(NS);
  const className = `campaign-route-skeleton${
    standalone ? ' campaign-route-skeleton--standalone' : ''
  }`;

  if (kind === 'create') {
    return (
      <SkeletonStatus
        label={t('loading.wizard')}
        className={className}
        testId="campaign-route-skeleton"
      >
        <CreateSkeleton />
      </SkeletonStatus>
    );
  }

  return (
    <div className={className} data-testid="campaign-route-skeleton">
      {kind === 'list' && <ListSkeleton />}
      {kind === 'detail' && <DetailSkeleton />}
      {kind === 'results' && <ResultsSkeleton />}
    </div>
  );
};
